import _extends from "@babel/runtime/helpers/extends";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2;
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import styled from "@emotion/styled";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { useCombinedThemeMode } from "@smartsheet/sds-theme-internal";
import { useSelect } from "downshift";
import { useRef } from "react";
import { noop } from "../../../../constants/noop";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useFocused } from "../../hooks/useFocused";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { useSelectIds, stopPropagationHandler, isSelectV2OptionSeparator, findNextValidOptionIndex } from "../../utils";
import { SelectButton } from "../SelectButton";
import { SelectButtonText } from "../SelectButtonText";
import { selectBasicFontTheme, selectV2Theme } from "../selectV2Theme";
import { ActionButtonBaseCss } from "../shared/ActionButton";
import { Actions } from "../shared/Actions";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButtonIcon } from "../shared/ToggleButtonIcon";
import { ContentBeforeContainer } from "./ContentBeforeContainer";
// Necessary due to the computeRightPadding in SelectButton.tsx
var ActionsStyled = styled(Actions)(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    position: absolute;\n    right: 0;\n"])));
// The whole select is the toggle button here, so we add a toggle-button-looking thing for
// consistency. Users love consistency and they LOVE chevrons that point down.
// If action button is used, an axe error is thrown because toggle would have a focusable
// element inside it
var FakeActionButton = styled("span")(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", "\n    ", "\n    &:hover {\n        background-color: ", ";\n    }\n    &:active {\n        background-color: ", ";\n    }\n"])), function (_ref) {
  var themeMode = _ref.themeMode;
  return ActionButtonBaseCss({
    themeMode: themeMode
  });
}, function (_ref2) {
  var isOpen = _ref2.isOpen,
    themeMode = _ref2.themeMode;
  return isOpen ? "\n                background-color:  ".concat(selectV2Theme.actionButtons.background.active(themeMode), ";\n            ") : "";
}, function (_ref3) {
  var themeMode = _ref3.themeMode;
  return selectV2Theme.actionButtons.background.hover(themeMode);
}, function (_ref4) {
  var themeMode = _ref4.themeMode;
  return selectV2Theme.actionButtons.background.active(themeMode);
});
export var SelectBasic = function SelectBasic(_ref5) {
  var _ref5$appearance = _ref5.appearance,
    appearance = _ref5$appearance === void 0 ? "outlined" : _ref5$appearance,
    ariaDescribedby = _ref5["aria-describedby"],
    ariaLabel = _ref5["aria-label"],
    ariaLabelledby = _ref5["aria-labelledby"],
    classes = _ref5.classes,
    className = _ref5.className,
    clientId = _ref5.clientId,
    contentBefore = _ref5.contentBefore,
    formatOption = _ref5.formatOption,
    getHiddenInputValue = _ref5.getHiddenInputValue,
    hiddenInputName = _ref5.hiddenInputName,
    id = _ref5.id,
    innerRef = _ref5.innerRef,
    isLoading = _ref5.isLoading,
    isOptionDisabled = _ref5.isOptionDisabled,
    isReadonly = _ref5.isReadonly,
    _ref5$isClearable = _ref5.isClearable,
    isClearable = _ref5$isClearable === void 0 ? false : _ref5$isClearable,
    _ref5$menuWidth = _ref5.menuWidth,
    menuWidth = _ref5$menuWidth === void 0 ? "match-control" : _ref5$menuWidth,
    maxMenuHeight = _ref5.maxMenuHeight,
    maxOptionsUntilVirtualization = _ref5.maxOptionsUntilVirtualization,
    _ref5$menuPlacement = _ref5.menuPlacement,
    menuPlacement = _ref5$menuPlacement === void 0 ? "bottom" : _ref5$menuPlacement,
    menuPosition = _ref5.menuPosition,
    menuId = _ref5.menuId,
    _ref5$onBlur = _ref5.onBlur,
    providedOnBlur = _ref5$onBlur === void 0 ? noop : _ref5$onBlur,
    onChange = _ref5.onChange,
    _ref5$onFocus = _ref5.onFocus,
    providedOnFocus = _ref5$onFocus === void 0 ? noop : _ref5$onFocus,
    optionHeight = _ref5.optionHeight,
    options = _ref5.options,
    optionToString = _ref5.optionToString,
    portalProps = _ref5.portalProps,
    selectedOption = _ref5.selectedOption,
    getOptionKey = _ref5.getOptionKey,
    validationState = _ref5.validationState,
    menuIsOpen = _ref5.menuIsOpen,
    setMenuIsOpen = _ref5.setMenuIsOpen,
    onMenuIsOpenChanged = _ref5.onMenuIsOpenChanged,
    _ref5$hideDropdownArr = _ref5.hideDropdownArrow,
    hideDropdownArrow = _ref5$hideDropdownArr === void 0 ? false : _ref5$hideDropdownArr,
    formatSelectedValue = _ref5.formatSelectedValue,
    placeholder = _ref5.placeholder,
    providedOnKeyDown = _ref5.onKeyDown;
  var a11yMessages = useA11yStatusMessages();
  var themeMode = useCombinedThemeMode();
  var isInteractive = !isReadonly;
  //TODO: implement this with the new ROOT
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  var _useFocused = useFocused({
      onFocus: providedOnFocus,
      onBlur: providedOnBlur
    }),
    isFocused = _useFocused.isFocused,
    onBlur = _useFocused.onBlur,
    onFocus = _useFocused.onFocus;

  // The flyout needs to point to the component container, but otherwise flyout interactions
  // should affect the input.
  var _useHorizontalResize = useHorizontalResize(),
    resizeRef = _useHorizontalResize.ref,
    targetWidth = _useHorizontalResize.width;
  var localToggleRef = useRef(null);
  var toggleButtonRef = useForkRef(localToggleRef, resizeRef, innerRef || null);
  var _useSelect = useSelect(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, a11yMessages), useSelectIds("select", id)), menuIsOpen !== undefined ? {
      isOpen: menuIsOpen
    } : null), {}, {
      selectedItem: null,
      items: options,
      circularNavigation: false,
      itemToString: optionToString,
      stateReducer: function stateReducer(state, _ref6) {
        var changes = _ref6.changes,
          type = _ref6.type;
        switch (type) {
          case useSelect.stateChangeTypes.MenuKeyDownEscape:
            // Cancel the escape key handling. We want to handle this key on our own
            // through the StackOrderManager.
            return state;
          case useSelect.stateChangeTypes.MenuKeyDownEnter:
          case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
          case useSelect.stateChangeTypes.ItemClick:
            if (changes.selectedItem) {
              return isOptionDisabled !== null && isOptionDisabled !== void 0 && isOptionDisabled(changes.selectedItem) ? state : changes;
            }
        }
        if (changes.highlightedIndex) {
          switch (type) {
            case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
            case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
              if (isSelectV2OptionSeparator(options[changes.highlightedIndex])) {
                return _objectSpread(_objectSpread({}, changes), {}, {
                  highlightedIndex: findNextValidOptionIndex(options, changes.highlightedIndex, type === useSelect.stateChangeTypes.MenuKeyDownArrowDown)
                });
              }
          }
        }
        return changes;
      },
      onStateChange: function onStateChange(_ref7) {
        var type = _ref7.type,
          selectedItem = _ref7.selectedItem;
        switch (type) {
          case useSelect.stateChangeTypes.MenuKeyDownEscape:
          case useSelect.stateChangeTypes.MenuKeyDownEnter:
          case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
          case useSelect.stateChangeTypes.ItemClick:
            if (selectedItem) {
              // TODO are there cases here where the selectedItem is null?
              onChange(selectedItem);
            }
            break;
        }
      },
      onIsOpenChange: function onIsOpenChange(_ref8) {
        var _ref9;
        var openState = _ref8.isOpen;
        if (process.env.NODE_ENV !== "production") {
          var _warnOnceForDeprecate;
          warnOnceForDeprecatedSetMenuIsOpenProp = (_warnOnceForDeprecate = warnOnceForDeprecatedSetMenuIsOpenProp) !== null && _warnOnceForDeprecate !== void 0 ? _warnOnceForDeprecate : warnOnce("SelectV2 (basic): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead.");
          if (setMenuIsOpen) {
            warnOnceForDeprecatedSetMenuIsOpenProp();
          }
        }
        // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
        (_ref9 = onMenuIsOpenChanged !== null && onMenuIsOpenChanged !== void 0 ? onMenuIsOpenChanged : setMenuIsOpen) === null || _ref9 === void 0 ? void 0 : _ref9(!!openState);
      }
    })),
    closeMenu = _useSelect.closeMenu,
    getItemProps = _useSelect.getItemProps,
    getMenuProps = _useSelect.getMenuProps,
    getToggleButtonProps = _useSelect.getToggleButtonProps,
    highlightedIndex = _useSelect.highlightedIndex,
    isOpen = _useSelect.isOpen,
    openMenu = _useSelect.openMenu;
  var _useSelectFlyout = useSelectFlyout({
      menuId: menuId,
      clientId: clientId,
      closeMenu: closeMenu,
      isOpen: isOpen,
      targetRef: toggleButtonRef,
      menuPlacement: menuPlacement,
      menuPosition: menuPosition,
      portalProps: portalProps,
      updateKey: String(isOpen)
    }),
    flyoutProps = _useSelectFlyout.flyoutProps,
    targetRef = _useSelectFlyout.targetRef;
  var toggleButtonProps = getToggleButtonProps({
    // This will remove some mouse and key handling.
    // Unlike useCombobox, we don't need to handle chip navigation which these handlers
    // assist with.
    disabled: !isInteractive,
    ref: targetRef,
    onClick: stopPropagationHandler,
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyDown: function onKeyDown(e) {
      if (providedOnKeyDown) {
        providedOnKeyDown(e, {
          highlightedIndex: highlightedIndex,
          options: options
        });
      }
    },
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    id: id
  });

  // We don't ever want to include this; the button must still be focusable.
  delete toggleButtonProps["disabled"];
  return jsx(Root, {
    "data-client-id": getClientId(clientId, "-root"),
    className: className,
    isFocused: isFocused || isOpen,
    validationState: validationState || "rest",
    isReadonly: isReadonly,
    themeMode: themeMode,
    appearance: appearance
  }, jsx(HiddenInput, {
    clientId: clientId,
    selectionType: selectedOption,
    getHiddenInputValue: getHiddenInputValue,
    hiddenInputName: hiddenInputName
  }), jsx(SelectButton, _extends({
    type: "button",
    "data-menu-test-id": menuId,
    clientId: getClientId(clientId, "-toggle"),
    hideDropdownArrow: hideDropdownArrow,
    isClearable: isClearable,
    hasContentBefore: !!contentBefore,
    themeMode: themeMode
    // We'll reattach our own rather than relying on Clickable's ring.
    ,
    omitFocusRing: true
  }, toggleButtonProps), contentBefore ? jsx(ContentBeforeContainer, null, contentBefore) : null, formatSelectedValue !== undefined ? formatSelectedValue({
    option: selectedOption
  }) : jsx(SelectButtonText, _extends({}, selectBasicFontTheme.variant[themeMode], {
    themeMode: themeMode,
    lineClamp: 1
  }), optionToString(selectedOption) || ""), selectedOption === null && placeholder && jsx(SelectPlaceholder, {
    hasContentBefore: !!contentBefore
  }, placeholder)), isInteractive && jsx(ActionsStyled
  // This will not be rendered if the control is not interactive.
  , {
    isReadonly: false,
    themeMode: themeMode
  }, !hideDropdownArrow && jsx(FakeActionButton, {
    isOpen: isOpen,
    themeMode: themeMode,
    onMouseDown: function onMouseDown(e) {
      if (e.detail >= 2) {
        // If this is a doubleclick's mousedown, prevent text
        // highlighting. This is only an issue because we're pretending
        // this span is a button. Otherwise, the button would block
        // text selection on double click.
        e.preventDefault();
      }
    },
    onClick: function onClick() {
      if (!isOpen) {
        openMenu();
      } else {
        closeMenu();
      }
    },
    onMouseUp: function onMouseUp(event) {
      if (isOpen) {
        // If we don't stop propagation here, downshift will see this
        // mouseup as a click outside the component since it's floating
        // outside the toggle button. Downshift will then attempt to close
        // the menu. Since we want to call openMenu/closeMenu in onClick
        // ourselves, we need to tell downshift to chill.
        event.stopPropagation();
      }
    }
  }, jsx(ToggleButtonIcon, null)), isClearable && selectedOption && jsx(SelectClearButton, {
    clearSelection: onChange,
    isMultiSelect: false,
    resetFiltering: function resetFiltering() {},
    refocusTargetElement: function refocusTargetElement() {
      var _localToggleRef$curre;
      (_localToggleRef$curre = localToggleRef.current) === null || _localToggleRef$curre === void 0 ? void 0 : _localToggleRef$curre.focus();
    }
  })), jsx(SelectMenu, {
    allSelected: false,
    enableSelectAll: false,
    extraMenuProps: {
      onKeyDown: function onKeyDown(e) {
        var _localToggleRef$curre2;
        providedOnKeyDown === null || providedOnKeyDown === void 0 ? void 0 : providedOnKeyDown(e, {
          highlightedIndex: highlightedIndex,
          options: options
        });
        switch (e.key) {
          case "Tab":
            e.preventDefault();
            (_localToggleRef$curre2 = localToggleRef.current) === null || _localToggleRef$curre2 === void 0 ? void 0 : _localToggleRef$curre2.focus();
            break;
        }
      }
    },
    flyoutProps: flyoutProps,
    formatOption: formatOption,
    getItemProps: getItemProps,
    getMenuProps: getMenuProps,
    className: classes === null || classes === void 0 ? void 0 : classes.menu,
    highlightedIndex: highlightedIndex,
    inputValue: "",
    isLoading: isLoading,
    isOpen: isOpen,
    isOptionDisabled: isOptionDisabled,
    menuWidth: menuWidth,
    maxMenuHeight: maxMenuHeight,
    maxOptionsUntilVirtualization: maxOptionsUntilVirtualization,
    optionHeight: optionHeight,
    options: options,
    optionToString: optionToString,
    selectedItems: [],
    selectedOption: selectedOption,
    targetWidth: targetWidth,
    getOptionKey: getOptionKey
  }));
};
var warnOnceForDeprecatedSetMenuIsOpenProp;
//# sourceMappingURL=SelectBasic.js.map